import { z } from "zod";

/**
 * SKU 事实包十项。顺序与主计划 §3.3 的表一致，改动必须两边同步。
 * 这个包只记录"用户/页面能证明的事实"，不是模型生成的描述。
 */
export const SKU_FACT_IDS = [
  "identity",
  "confirmed_facts",
  "claim_evidence",
  "final_copy",
  "content_sku",
  "ai_disclosure",
  "rights",
  "restricted",
  "offsite_links",
  "owner_review",
] as const;
export const SkuFactIdSchema = z.enum(SKU_FACT_IDS);
export type SkuFactId = z.infer<typeof SkuFactIdSchema>;

export const SKU_FACT_TEMPLATE: Array<{ id: SkuFactId; label: string; check: string }> = [
  { id: "identity", label: "商品身份与归属", check: "Product ID、变体、类目与品牌和实际 SKU 一致；类目准入与品牌授权资料齐备" },
  { id: "confirmed_facts", label: "已确认商品事实与规格", check: "材质、尺寸、重量等规格有据可查，不靠推测补齐" },
  { id: "claim_evidence", label: "卖点说法与证据", check: "治疗、防水、认证、畅销款、保证有效等表述都有当前有效证据" },
  { id: "final_copy", label: "现行文案与批准图片", check: "待发布内容与已复核的终稿、已批准图片一致" },
  { id: "content_sku", label: "内容与挂车 SKU", check: "视频或直播展示的商品与挂车 SKU 一致" },
  { id: "ai_disclosure", label: "AI 生成与披露状态", check: "完全或大幅 AI 编辑的内容已按平台政策披露" },
  { id: "rights", label: "素材与授权", check: "图片、肖像、声音、音乐的使用授权已确认" },
  { id: "restricted", label: "受限、受监管与冒充风险", check: "是否触及受限类目、受监管商品或冒充他人品牌；命中即暂停自动化" },
  { id: "offsite_links", label: "站外引流检查", check: "Listing、包装卡与卖家沟通都不导向站外联系方式或平台" },
  { id: "owner_review", label: "负责人与最后复核日期", check: "有人对这份事实包负责，且复核日期可追溯" },
];
export const SKU_FACT_TEMPLATE_BY_ID = new Map(SKU_FACT_TEMPLATE.map((item) => [item.id, item]));

export const SkuFactEntrySchema = z.object({
  id: SkuFactIdSchema,
  label: z.string().min(1).max(120),
  check: z.string().min(1).max(300),
  status: z.enum(["confirmed", "missing", "needs_review"]),
  owner: z.string().min(1).max(80),
  reviewedAt: z.string(),
  note: z.string().max(300).optional(),
}).strict();
export type SkuFactEntry = z.infer<typeof SkuFactEntrySchema>;

export const SkuFactPackSchema = z.object({
  facts: z.array(SkuFactEntrySchema).max(SKU_FACT_IDS.length),
  updatedAt: z.string(),
}).strict().superRefine((value, context) => {
  const seen = new Set<SkuFactId>();
  for (const [index, fact] of value.facts.entries()) {
    if (seen.has(fact.id)) context.addIssue({ code: "custom", path: ["facts", index, "id"], message: `事实包出现重复项：${fact.id}` });
    seen.add(fact.id);
  }
  for (const id of SKU_FACT_IDS) {
    if (!seen.has(id)) context.addIssue({ code: "custom", path: ["facts"], message: `事实包缺少第 ${SKU_FACT_IDS.indexOf(id) + 1} 项：${id}` });
  }
});
export type SkuFactPack = z.infer<typeof SkuFactPackSchema>;

export const ComplianceCheckKindSchema = z.enum([
  "fact_pack", "prohibited_term", "absolute_claim", "brand_term",
  "asset_spec", "attribute", "restricted", "offsite_link",
  /** 规则包自己声明"未逐条核实"时的声明项：预检通过不等于平台规则已核对。 */
  "platform_rules",
]);
export type ComplianceCheckKind = z.infer<typeof ComplianceCheckKindSchema>;
export const ComplianceSeveritySchema = z.enum(["blocker", "warning", "info"]);
export type ComplianceSeverity = z.infer<typeof ComplianceSeveritySchema>;

export const ComplianceCheckSchema = z.object({
  kind: ComplianceCheckKindSchema,
  severity: ComplianceSeveritySchema,
  field: z.string().min(1).max(120).optional(),
  finding: z.string().min(1).max(400),
  suggestion: z.string().min(1).max(300).optional(),
}).strict();
export type ComplianceCheck = z.infer<typeof ComplianceCheckSchema>;

/** 预检口径固定，不允许被编辑或替换：只查格式、完整性与明显矛盾，不承诺过审。 */
export const LISTING_COMPLIANCE_DISCLAIMER =
  "预检只检查格式、完整性和明显矛盾，不承诺审核通过；是否通过以平台官方审核结果为准。";

/** 承诺性措辞：预检结果自己也不能出现这些说法。 */
export const COMPLIANCE_PROMISE_PATTERN = /(保证通过|一定过审|百分百过|必过|包过|确保过审)/;

export const ListingComplianceResultSchema = z.object({
  platform: z.string().min(1).max(80),
  category: z.string().max(120).optional(),
  status: z.enum(["passed", "blocked", "needs_input"]),
  checks: z.array(ComplianceCheckSchema).max(40),
  /** 命中受限/受监管/冒充风险时为 false：此时不得给出任何自动化建议。 */
  automatedSuggestionsAllowed: z.boolean(),
  blockedReasons: z.array(z.string().min(1).max(300)).max(8),
  disclaimer: z.literal(LISTING_COMPLIANCE_DISCLAIMER),
  reviewedAt: z.string(),
  nextReviewAt: z.string(),
  sources: z.array(z.string().min(1).max(600)).max(8),
}).strict().superRefine((value, context) => {
  if (!value.automatedSuggestionsAllowed) {
    for (const [index, check] of value.checks.entries()) {
      if (check.suggestion !== undefined) {
        context.addIssue({ code: "custom", path: ["checks", index, "suggestion"], message: "禁止自动化建议时不得给出任何建议" });
      }
    }
  }
  for (const [index, check] of value.checks.entries()) {
    if (COMPLIANCE_PROMISE_PATTERN.test(`${check.finding} ${check.suggestion ?? ""}`)) {
      context.addIssue({ code: "custom", path: ["checks", index], message: "预检结果不得承诺过审" });
    }
  }
  const blocked = value.checks.some((check) => check.severity === "blocker");
  if (blocked && value.status !== "blocked") context.addIssue({ code: "custom", path: ["status"], message: "存在阻断项时状态必须是 blocked" });
  if (value.status === "blocked" && value.blockedReasons.length === 0) {
    context.addIssue({ code: "custom", path: ["blockedReasons"], message: "blocked 必须说明原因" });
  }
});
export type ListingComplianceResult = z.infer<typeof ListingComplianceResultSchema>;

/** 能力包：平台相关的检查依据。没有核实到的部分必须降级，不得编造数值。 */
export const ListingAssetSpecSchema = z.object({
  id: z.enum(["size", "ratio", "clarity", "text_overlay", "background", "watermark"]),
  label: z.string().min(1).max(60),
  inspectable: z.string().min(1).max(200),
  verified: z.boolean(),
  note: z.string().min(1).max(240),
}).strict();

export const ListingComplianceRulesSchema = z.object({
  rulePackId: z.string().min(1).max(120),
  platform: z.string().min(1).max(80),
  category: z.string().max(120).optional(),
  status: z.enum(["verified", "needs_review", "needs_market"]),
  reviewedAt: z.string(),
  nextReviewAt: z.string(),
  termLists: z.array(z.object({
    kind: ComplianceCheckKindSchema,
    label: z.string().min(1).max(60),
    terms: z.array(z.string().min(1).max(60)).max(80),
    basis: z.string().min(1).max(300),
    sourceUrl: z.string().url().max(600).optional(),
  }).strict()).max(4),
  assetSpecs: z.array(ListingAssetSpecSchema).max(6),
  requiredAttributes: z.array(z.string().min(1).max(60)).max(30),
  restrictedCategories: z.array(z.string().min(1).max(80)).max(30),
  notices: z.array(z.string().min(1).max(300)).max(8),
  blockingIssues: z.array(z.string().min(1).max(300)).max(6),
}).strict();
export type ListingComplianceRules = z.infer<typeof ListingComplianceRulesSchema>;
